import { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { BlockHead, useExpand } from './Expand.tsx';
import type { TableBlock as TableBlockT } from '@aibify/core';
import { fmtCell, idCell, isNegative, isNumericType, planColumns, type ViewCol } from './format.ts';
import { buildXlsx } from './xlsx.ts';
import { downloadBlob } from './png.ts';
import { useDisplay } from './context.tsx';
import { S } from './strings.ts';
import {
  CONTROLS_MIN_ROWS,
  TABLE_HE,
  columnTotals,
  createTableControls,
  isActive,
  kindOf,
  nextSort,
  searchIndex,
  viewIdx,
  viewNote,
  type TableControls,
  type TableState,
} from './kit/table.js';

const PAGE = 200;
/** web-screen-budget הכרעה 9: טבלה בשיחה — 12 שורות (≈ 500px ב-1512), השאר בהגדלה. */
export const COMPACT_ROWS = 12;

/**
 * גלילה אופקית: כשיש עוד עמודות מעבר לקצה ה-end (שמאל ב-RTL), הקצה מקבל דהייה (data-more-end) — אחרת עמודה שנחתכת
 * בדיוק בקצה המסך נראית כמו כותרת קטועה (למשל 'סה"כ' שמוצג כ-'סה': הטקסט שלם, העמודה נחתכה באמצע). scrollLeft ב-RTL ≤ 0.
 */
function useOverflowEdge() {
  const ref = useRef<HTMLDivElement>(null);
  const [moreEnd, setMoreEnd] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      const rtl = getComputedStyle(el).direction === 'rtl';
      const pos = rtl ? -el.scrollLeft : el.scrollLeft;
      setMoreEnd(max > 1 && pos < max - 1);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    if (el.firstElementChild) ro?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
    };
  }, []);
  return { ref, moreEnd };
}

function Cell({ row, c }: { row: Record<string, unknown>; c: ViewCol }) {
  if (c.type === 'id') {
    const { code, desc } = idCell(row, c);
    return (
      <td className="ab-id" data-col={c.key}>
        <bdi dir="auto">{code}</bdi>
        {desc ? (
          <>
            <span className="ab-sep" aria-hidden="true">
              {' · '}
            </span>
            <bdi dir="auto">{desc}</bdi>
          </>
        ) : (
          <span className="ab-nodesc" title={S.noDescTitle}>
            {S.noDesc}
          </span>
        )}
      </td>
    );
  }
  const v = row[c.key];
  const num = c.align ? c.align === 'end' : isNumericType(c.type);
  const cls = [num ? 'num' : '', isNumericType(c.type) && isNegative(v) ? 'neg' : ''].filter(Boolean).join(' ');
  return (
    <td className={cls || undefined} data-col={c.key}>
      {/* מספר שזוהה בעמודת טקסט ("-79", "1,200") — LTR, אחרת המינוס קופץ לסוף */}
      <bdi dir={num && c.type === 'text' ? 'ltr' : 'auto'}>{fmtCell(v, c.type)}</bdi>
    </td>
  );
}

export type TableView = { rows: Row[]; note: string };
type Row = Record<string, unknown>;

/**
 * מיון (לחיצה על כותרת: כיוון ראשון → הפוך → בלי), חיפוש בכל העמודות וסינון לפי עמודה (משפך בכותרת → לוח מתחת לשורת החיפוש;
 * סינונים פעילים כשבבים). הלוגיקה והפקדים — kit/table.js, אותו קוד כמו ab.table ב-iframe. ב-PDF (print): כל השורות, בלי פקדים.
 * viewRef: השורות שבתצוגה + שורת הסבר — לייצוא Excel מבחוץ (PairView).
 */
export function TableBlock({ block, bare = false, viewRef }: { block: TableBlockT; bare?: boolean; viewRef?: MutableRefObject<TableView | null> }) {
  // עמודת text שהערכים שלה מספרים (מזוהה מהנתונים, kit/table.js kindOf) — מיושרת לסוף כמו מספר
  const cols = useMemo(
    () => planColumns(block.columns).map((c) => (!c.align && c.type === 'text' && kindOf(c, block.rows) === 'num' ? { ...c, align: 'end' as const } : c)),
    [block.columns, block.rows],
  );
  const { print, compact } = useDisplay();
  const [limit, setLimit] = useState(print ? Infinity : PAGE);
  // compact (בשיחה): 12 שורות ו"עוד N שורות" שפותח את ההגדלה של הבלוק — שם (אותו אלמנט, אותו מיון/סינון) כל השורות
  const ex = useExpand();
  const isCompact = !!compact && !print && !!ex && !ex.expanded;
  const [st, setSt] = useState<TableState>(() => ({ q: '', filters: {}, sort: block.sort ?? null }));
  const stRef = useRef(st);
  stRef.current = st;
  const controls = !print && (block.filters === undefined ? block.rows.length >= CONTROLS_MIN_ROWS : block.filters);
  const index = useMemo(() => (controls ? searchIndex(block.rows, cols, fmtCell) : null), [block.rows, cols, controls]);
  const view = useMemo(() => viewIdx(block.rows, cols, st, fmtCell, index).map((i) => block.rows[i]!), [block.rows, cols, st, index]);
  const rows = view.slice(0, isCompact ? COMPACT_ROWS : limit);
  const more = view.length - rows.length;
  const ownView = useRef<TableView | null>(null);
  const vRef = viewRef ?? ownView;
  vRef.current = { rows: view, note: viewNote(st, cols, view.length, block.rows.length, fmtCell, block.rows) };
  const exportXlsx = useXlsxExport(block, vRef);
  const edge = useOverflowEdge();
  const totals = useMemo(() => (block.totals ? columnTotals(view, cols) : null), [block.totals, view, cols]);

  // פקדים (DOM משותף מ-kit): נבנים פעם אחת לנתונים; המצב זורם אליהם ב-update
  const ctrlHost = useRef<HTMLDivElement>(null);
  const ctrl = useRef<TableControls | null>(null);
  const fbtns = useRef<Record<string, HTMLButtonElement | null>>({});
  const [openKey, setOpenKey] = useState<string | null>(null);
  useEffect(() => {
    if (!controls || !ctrlHost.current) return;
    const c = createTableControls(ctrlHost.current, {
      columns: cols,
      rows: block.rows,
      state: stRef.current,
      fmt: fmtCell,
      index,
      onChange: (s) => setSt((prev) => ({ ...prev, q: s.q ?? '', filters: s.filters ?? {} })),
      onClose: (key, refocus) => {
        setOpenKey(null);
        if (refocus !== false) fbtns.current[key]?.focus();
      },
    });
    ctrl.current = c;
    return () => {
      c.destroy();
      ctrl.current = null;
      setOpenKey(null);
    };
  }, [controls, cols, block.rows, index]);
  useEffect(() => {
    ctrl.current?.update(st, view.length);
  }, [st, view.length, controls, cols, block.rows]);

  const toggleFilter = (key: string) => {
    const c = ctrl.current;
    if (!c) return;
    if (c.openKey() === key) c.close();
    else {
      setOpenKey(key);
      c.open(key, fbtns.current[key]);
    }
  };
  const isNum = (c: ViewCol) => (c.align ? c.align === 'end' : isNumericType(c.type));

  const body = (
    <>
      {controls && <div ref={ctrlHost} className="abt-slot" />}
      {/* ab-table-wrap: מיכל (container query) — בבלוק רחב מאוד הטבלה ברוחב התוכן, מיושרת לתחילת השורה (web-ai-stage-layout) */}
      <div className="ab-table-wrap">
        <div ref={edge.ref} className="ab-table-scroll" data-testid="table-scroll" data-more-end={edge.moreEnd || undefined} tabIndex={0} role="region" aria-label={block.titleHe ?? S.table}>
          <table className="ab-table">
            <thead>
              <tr>
                {cols.map((c) => {
                  const sorted = st.sort?.key === c.key ? st.sort.dir : null;
                  const filtered = isActive(st.filters?.[c.key]);
                  return (
                    <th
                      key={c.key}
                      scope="col"
                      className={isNum(c) ? 'num' : undefined}
                      aria-sort={sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : undefined}
                      data-filtered={filtered || undefined}
                    >
                      <span className="abt-th">
                        {/* ב-PDF: span ולא button — Chromium לא חוזר על thead בכל עמוד כשיש בתוכו form control (נמדד 2026-10-15) */}
                        {print ? (
                          <span className="abt-sort">{c.labelHe}</span>
                        ) : (
                          <button type="button" className="abt-sort" onClick={() => setSt((s) => ({ ...s, sort: nextSort(s.sort, c, block.rows) }))}>
                            {c.labelHe}
                          </button>
                        )}
                        {controls && (
                          <button
                            type="button"
                            className="abt-fbtn"
                            ref={(el) => {
                              fbtns.current[c.key] = el;
                            }}
                            aria-label={TABLE_HE.filterBy(c.labelHe)}
                            aria-expanded={openKey === c.key}
                            aria-haspopup="dialog"
                            data-active={filtered || undefined}
                            onClick={() => toggleFilter(c.key)}
                          />
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  {cols.map((c) => (
                    <Cell key={c.key} row={row} c={c} />
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={cols.length || 1} className="ab-empty">
                    {block.rows.length ? TABLE_HE.noMatch : S.noRows}
                  </td>
                </tr>
              )}
            </tbody>
            {totals && (
              <tfoot>
                <tr className="ab-total">
                  {cols.map((c, ci) => (
                    <td key={c.key} className={c.key in totals && isNum(c) ? 'num' : undefined} data-col={c.key}>
                      {c.key in totals ? <bdi dir="auto">{fmtCell(totals[c.key], c.type)}</bdi> : ci === 0 ? TABLE_HE.total : null}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      {more > 0 &&
        (isCompact ? (
          <button type="button" className="ab-btn ab-more" onClick={ex!.open} data-testid="table-more-rows" aria-haspopup="dialog">
            {S.moreRows(more)}
          </button>
        ) : (
          <button type="button" className="ab-btn ab-more" onClick={() => setLimit((l) => l + PAGE)}>
            {S.showMore(more)}
          </button>
        ))}
      {block.truncated && <p className="ab-truncated">{S.truncated(block.rows.length, block.totalRows)}</p>}
    </>
  );
  if (bare) return body;
  return (
    <section className="ab-block" data-block-type="table" data-block-id={block.id}>
      <BlockHead titleHe={block.titleHe}>
        <button type="button" className="ab-btn ab-btn-ghost" onClick={exportXlsx}>
          {S.exportXlsx}
        </button>
      </BlockHead>
      {body}
    </section>
  );
}

/** ייצוא Excel: כשיש viewRef — מה שעל המסך (אחרי חיפוש/סינון/מיון) + שורת הסבר; אחרת כל השורות. */
export function useXlsxExport(block: TableBlockT, viewRef?: MutableRefObject<TableView | null>) {
  const { asOfText, exportedBy } = useDisplay();
  return () => {
    const v = viewRef?.current;
    const b = v ? { ...block, rows: v.rows } : block;
    downloadBlob(buildXlsx(b, { asOfText, exportedBy, noteHe: v?.note || undefined }), `${block.titleHe ?? block.id}.xlsx`);
  };
}
